import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  Typography,
} from '@mui/material';
import { useDivisionOptions, useTeamOptions } from '../../../state/master';
import {
  API_ENDPOINTS,
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';
import { apiClient } from '../../../lib/apiClient';

type Props = {
  open: boolean;
  division: number;
  canEdit?: boolean;
  onClose: () => void;
};

type TeamLite = {
  teamId: number;
  teamName: string;
};

type TeamDocsetSummary = {
  fetchOk: boolean;
  hasLinkedEnlightTeam: boolean;
  linkedEnlightTeam: string;
  linkedDocsetId: string | number | null;
  hasWrInputAuthor: boolean;
};

const FIXED_ATTR_ID_SET = new Set<number>([1, 2, 3, 4, 5, 6]);

// 固定項目のattr_idは課・チームごとに枝番化されるため、attr_idが一致しない場合はこの名称で拾い直す
const FIXED_ATTR_FALLBACK_NAME_BY_ID: Record<number, string> = {
  1: CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  2: FIXED_ATTR_NAME_PROJECT,
  3: FIXED_ATTR_NAME_POWERTRAIN,
  4: '自由入力欄',
  5: FIXED_ATTR_NAME_SHIMUKE,
  6: FIXED_ATTR_NAME_LOT,
};

const norm = (v: any): string => String(v ?? '').trim();
const getAttrNameKey = (r: any): string => norm((r as any)?.attr_name ?? (r as any)?.label);
const isEnabled = (status: any): boolean => String(status ?? 'Y').toUpperCase() !== 'N';
const normalizeEnlightTeam = (v: any): string => {
  const s = norm(v);
  if (!s) return '';
  const lc = s.toLowerCase();
  if (lc === 'none' || lc === 'null') return '';
  return s;
};

const extractEnlightTeamFromDocset = (data: any): string => {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : data
          ? [data]
          : [];
  if (!Array.isArray(arr) || arr.length === 0) {
    return normalizeEnlightTeam((data as any)?.enlight_team ?? (data as any)?.enlightTeam);
  }
  const hit = arr.find((x: any) => normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam));
  if (!hit) return '';
  return normalizeEnlightTeam((hit as any)?.enlight_team ?? (hit as any)?.enlightTeam);
};

const extractDocsetIdFromDocset = (data: any): string | number | null => {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : data
          ? [data]
          : [];

  const pick = Array.isArray(arr)
    ? arr.find((x: any) => normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam)) ?? arr[0]
    : null;

  const raw = (pick as any)?.docset_id ?? (pick as any)?.docsetId ?? (data as any)?.docset_id ?? (data as any)?.docsetId;
  if (raw === undefined || raw === null || String(raw).trim() === '') return null;
  return raw;
};

const extractTeamDocsetSummary = (data: any): TeamDocsetSummary => {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : data
          ? [data]
          : [];

  const hasLinkedInRows = Array.isArray(arr)
    ? arr.some((x: any) => Boolean(normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam)))
    : false;
  const hasLinkedRoot = Boolean(normalizeEnlightTeam((data as any)?.enlight_team ?? (data as any)?.enlightTeam));
  const hasLinkedEnlightTeam = hasLinkedInRows || hasLinkedRoot;

  const hasWrInputAuthor = Array.isArray(arr)
    ? arr.some((x: any) => {
        const s = norm((x as any)?.author_system).toLowerCase();
        return s === 'wr_input_app' || s === 'weekly-report-input-app';
      })
    : false;

  return {
    fetchOk: true,
    hasLinkedEnlightTeam,
    linkedEnlightTeam: hasLinkedEnlightTeam ? extractEnlightTeamFromDocset(data) : '',
    linkedDocsetId: extractDocsetIdFromDocset(data),
    hasWrInputAuthor,
  };
};

const signatureFromLabels = (labels: string[]): string => {
  return Array.from(new Set(labels.map((x) => x.trim()).filter(Boolean))).sort().join('||');
};

const uniqueSortedLabels = (labels: string[]): string[] => {
  return Array.from(new Set(labels.map((x) => x.trim()).filter(Boolean))).sort();
};

const EnlightIDConnectionCopyDialog: React.FC<Props> = ({ open, division, canEdit = true, onClose }) => {
  const { divisionOptions } = useDivisionOptions();
  const { teamOptionsRaw } = useTeamOptions();

  const [loadingAttributes, setLoadingAttributes] = React.useState(false);
  const [loadingSourceTeams, setLoadingSourceTeams] = React.useState(false);
  const [attributeRows, setAttributeRows] = React.useState<any[]>([]);
  const [linkedSourceTeams, setLinkedSourceTeams] = React.useState<Array<{ teamId: number; teamName: string; enlightTeam: string }>>([]);
  const [teamDocsetSummaryByTeamId, setTeamDocsetSummaryByTeamId] = React.useState<Record<number, TeamDocsetSummary>>({});
  const [selectedSourceTeamId, setSelectedSourceTeamId] = React.useState<string>('');
  const [selectedTargetTeamIds, setSelectedTargetTeamIds] = React.useState<Record<number, boolean>>({});
  const [submittingCopy, setSubmittingCopy] = React.useState(false);
  const [copyResultMessage, setCopyResultMessage] = React.useState<string>('');
  const [reloadSeq, setReloadSeq] = React.useState(0);

  const divisionLabel = React.useMemo(() => {
    const found = (divisionOptions ?? []).find(
      (d: any) => Number((d as any)?.department_id ?? (d as any)?.id) === Number(division)
    );
    return norm((found as any)?.label ?? (found as any)?.value);
  }, [divisionOptions, division]);

  const teamsInDivision = React.useMemo<TeamLite[]>(() => {
    return (teamOptionsRaw ?? [])
      .filter((t: any) => isEnabled((t as any)?.status))
      .filter((t: any) => Number((t as any)?.department_id) === Number(division))
      .map((t: any) => ({
        teamId: Number((t as any)?.team_id ?? (t as any)?.id ?? 0),
        teamName: norm((t as any)?.label ?? (t as any)?.value),
      }))
      .filter((t) => t.teamId > 0 && t.teamName);
  }, [teamOptionsRaw, division]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      if (teamsInDivision.length === 0) {
        setLinkedSourceTeams([]);
        setTeamDocsetSummaryByTeamId({});
        return;
      }
      setLoadingSourceTeams(true);
      try {
        const nextSummaryByTeamId: Record<number, TeamDocsetSummary> = {};
        const list = await Promise.all(
          teamsInDivision.map(async (t) => {
            try {
              const res = await apiClient.get('/get_team_docset_list', {
                params: {
                  department_id: Number(division) || 0,
                  team_id: Number(t.teamId) || 0,
                },
              });
              const summary = extractTeamDocsetSummary(res?.data);
              nextSummaryByTeamId[t.teamId] = summary;
              const et = extractEnlightTeamFromDocset(res?.data);
              if (!et) return null;
              return { teamId: t.teamId, teamName: t.teamName, enlightTeam: et };
            } catch (e) {
              // console.warn('get_team_docset_list failed', { division, teamId: t.teamId, e });
              nextSummaryByTeamId[t.teamId] = {
                fetchOk: false,
                hasLinkedEnlightTeam: false,
                linkedEnlightTeam: '',
                linkedDocsetId: null,
                hasWrInputAuthor: false,
              };
              return null;
            }
          })
        );
        if (cancelled) return;
        const rows = list
          .filter((x): x is { teamId: number; teamName: string; enlightTeam: string } => Boolean(x))
          .sort((a, b) => a.teamName.localeCompare(b.teamName, 'ja'));
        setTeamDocsetSummaryByTeamId(nextSummaryByTeamId);
        setLinkedSourceTeams(rows);
      } finally {
        if (!cancelled) setLoadingSourceTeams(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, division, teamsInDivision, reloadSeq]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoadingAttributes(true);
      try {
        const res = await apiClient.get(API_ENDPOINTS.GET_ATTRIBUTES, {
          params: { department_id: Number(division) || 0 },
        });
        if (cancelled) return;
        const data = res?.data;
        const arr = Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.data)
            ? (data as any).data
            : Array.isArray((data as any)?.list)
              ? (data as any).list
              : [];
        setAttributeRows(Array.isArray(arr) ? arr : []);
      } catch (e) {
        // console.error('GET_ATTRIBUTES failed in EnlightIDConnectionCopyDialog', e);
        if (!cancelled) setAttributeRows([]);
      } finally {
        if (!cancelled) setLoadingAttributes(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, division]);

  React.useEffect(() => {
    if (!open) return;
    const first = linkedSourceTeams[0];
    setSelectedSourceTeamId(first ? String(first.teamId) : '');
    setSelectedTargetTeamIds({});
  }, [open, linkedSourceTeams, division]);

  const teamAttributeLabelMap = React.useMemo(() => {
    const rows = Array.isArray(attributeRows) ? attributeRows : [];

    const rowsForDivisionOrGlobal = rows.filter((r: any) => {
      const d = Number((r as any)?.department_id ?? 0);
      return d === 0 || d === Number(division);
    });

    const pickRowForFixedAttr = (teamId: number, attrId: number) => {
      // 固定項目のattr_idは課・チームごとに枝番化されるため、attr_id一致に加えて
      // 属性名（フォールバック名）での一致も許容する
      const fallbackName = FIXED_ATTR_FALLBACK_NAME_BY_ID[attrId];
      const matchesAttr = (r: any) =>
        Number((r as any)?.attr_id) === attrId ||
        (Boolean(fallbackName) && getAttrNameKey(r) === fallbackName);

      const byTeam = rowsForDivisionOrGlobal.find((r: any) =>
        matchesAttr(r) &&
        Number((r as any)?.department_id) === Number(division) &&
        Number((r as any)?.team_id ?? 0) === teamId
      );
      if (byTeam) return byTeam;

      const byDivision = rowsForDivisionOrGlobal.find((r: any) =>
        matchesAttr(r) &&
        Number((r as any)?.department_id) === Number(division) &&
        Number((r as any)?.team_id ?? 0) === 0
      );
      if (byDivision) return byDivision;

      const global = rowsForDivisionOrGlobal.find((r: any) =>
        matchesAttr(r) &&
        Number((r as any)?.department_id) === 0 &&
        Number((r as any)?.team_id ?? 0) === 0
      );
      return global;
    };

    const m = new Map<number, string[]>();
    teamsInDivision.forEach((teamRow) => {
      const labels: string[] = [];

      [1, 2, 3, 4, 5, 6].forEach((attrId) => {
        const hit = pickRowForFixedAttr(teamRow.teamId, attrId);
        if (!hit) return;
        if (!isEnabled((hit as any)?.status)) return;
        const label = norm((hit as any)?.label);
        if (!label) return;
        labels.push(label);
      });

      const fixedNameSet = new Set(Object.values(FIXED_ATTR_FALLBACK_NAME_BY_ID));
      rowsForDivisionOrGlobal.forEach((r: any) => {
        const attrId = Number((r as any)?.attr_id ?? 0);
        if (!Number.isFinite(attrId)) return;
        // 固定項目（枝番化されたattr_idも含む）はフリー項目側の集計から除外する
        if (FIXED_ATTR_ID_SET.has(attrId) || fixedNameSet.has(getAttrNameKey(r))) return;
        if (Number((r as any)?.department_id) !== Number(division)) return;
        if (Number((r as any)?.team_id ?? 0) !== teamRow.teamId) return;
        if (!isEnabled((r as any)?.status)) return;
        const label = norm((r as any)?.label);
        if (!label) return;
        labels.push(label);
      });

      m.set(teamRow.teamId, uniqueSortedLabels(labels));
    });
    return m;
  }, [attributeRows, division, teamsInDivision]);

  const teamAttributeSignatureMap = React.useMemo(() => {
    const m = new Map<number, string>();
    teamAttributeLabelMap.forEach((labels, teamId) => {
      m.set(teamId, signatureFromLabels(labels));
    });
    return m;
  }, [teamAttributeLabelMap]);

  const selectedSourceTeamIdNum = Number(selectedSourceTeamId || 0);
  const selectedSourceSignature = teamAttributeSignatureMap.get(selectedSourceTeamIdNum) ?? '';

  const candidateTargetTeams = React.useMemo(() => {
    if (!selectedSourceTeamIdNum || !selectedSourceSignature) return [];
    return teamsInDivision
      .filter((t) => t.teamId !== selectedSourceTeamIdNum)
      .filter((t) => (teamAttributeSignatureMap.get(t.teamId) ?? '') === selectedSourceSignature)
      .filter((t) => {
        const summary = teamDocsetSummaryByTeamId[t.teamId];
        // 取得成功したチームは表示対象（連携済みでも表示し、選択のみ不可）
        return Boolean(summary?.fetchOk);
      })
      .sort((a, b) => a.teamName.localeCompare(b.teamName, 'ja'));
  }, [teamsInDivision, selectedSourceTeamIdNum, selectedSourceSignature, teamAttributeSignatureMap, teamDocsetSummaryByTeamId]);

  const handleCheckTarget = (teamId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = Boolean(e.target.checked);
    setSelectedTargetTeamIds((prev) => ({ ...prev, [teamId]: checked }));
  };

  const selectedTargetTeamIdList = React.useMemo(() => {
    return Object.entries(selectedTargetTeamIds)
      .filter(([, checked]) => Boolean(checked))
      .map(([teamId]) => Number(teamId))
      .filter((teamId) => Number.isFinite(teamId) && teamId > 0);
  }, [selectedTargetTeamIds]);

  const sourceLinkedEnlightTeam = React.useMemo(() => {
    const sourceId = Number(selectedSourceTeamId || 0);
    if (!sourceId) return '';
    return String(teamDocsetSummaryByTeamId[sourceId]?.linkedEnlightTeam ?? '').trim();
  }, [selectedSourceTeamId, teamDocsetSummaryByTeamId]);

  const sourceLinkedDocsetId = React.useMemo(() => {
    const sourceId = Number(selectedSourceTeamId || 0);
    if (!sourceId) return null;
    return teamDocsetSummaryByTeamId[sourceId]?.linkedDocsetId ?? null;
  }, [selectedSourceTeamId, teamDocsetSummaryByTeamId]);

  const handleExecuteCopy = async () => {
    if (!canEdit || submittingCopy) return;

    const sourceId = Number(selectedSourceTeamId || 0);
    const targetIds = selectedTargetTeamIdList;
    if (!sourceId || targetIds.length === 0) return;

    const payload = {
      department_id: Number(division) || 0,
      source_team_id: sourceId,
      source_enlight_team: sourceLinkedEnlightTeam,
      source_docset_id: sourceLinkedDocsetId,
      target_team_ids: targetIds,
    };

    setSubmittingCopy(true);
    setCopyResultMessage('');
    try {
      await apiClient.post('/copy_team_docset', payload);
      setCopyResultMessage('ID連携を実行しました。');
      setSelectedTargetTeamIds({});
      setReloadSeq((prev) => prev + 1);
    } catch (e) {
      // console.error('copy_team_docset failed', e);
      setCopyResultMessage('ID連携の実行に失敗しました。');
    } finally {
      setSubmittingCopy(false);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    if (teamsInDivision.length === 0) return;
    if (teamAttributeLabelMap.size === 0) return;

    const payload = teamsInDivision.map((t) => ({
      team_id: t.teamId,
      team_name: t.teamName,
      labels: teamAttributeLabelMap.get(t.teamId) ?? [],
    }));
    // console.log('[EnlightIDConnectionCopyDialog] team-label-list', payload);
  }, [open, teamsInDivision, teamAttributeLabelMap]);

  React.useEffect(() => {
    if (!open) return;
    if (teamsInDivision.length === 0) return;

    const sourceId = Number(selectedSourceTeamId || 0);
    const sourceSig = teamAttributeSignatureMap.get(sourceId) ?? '';

    const detail = teamsInDivision.map((t) => {
      const sig = teamAttributeSignatureMap.get(t.teamId) ?? '';
      const summary = teamDocsetSummaryByTeamId[t.teamId];
      const sameSignature = Boolean(sourceSig) && sig === sourceSig;
      const statusOk = Boolean(summary?.fetchOk);
      const isSource = t.teamId === sourceId;
      const visibleInRight = !isSource && sameSignature && statusOk;
      return {
        team_id: t.teamId,
        team_name: t.teamName,
        is_source: isSource,
        same_signature_as_source: sameSignature,
        fetch_ok: Boolean(summary?.fetchOk),
        has_linked_enlight_team: Boolean(summary?.hasLinkedEnlightTeam),
        linked_enlight_team: String(summary?.linkedEnlightTeam ?? ''),
        linked_docset_id: summary?.linkedDocsetId ?? null,
        has_wr_input_author: Boolean(summary?.hasWrInputAuthor),
        status_filter_ok: statusOk,
        visible_in_right: visibleInRight,
      };
    });

    // console.log('[EnlightIDConnectionCopyDialog] team-docset-summary', teamDocsetSummaryByTeamId);
    // console.log('[EnlightIDConnectionCopyDialog] right-filter-detail', detail);
  }, [
    open,
    teamsInDivision,
    selectedSourceTeamId,
    teamAttributeSignatureMap,
    teamDocsetSummaryByTeamId,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enlight ID連携コピー</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Enlight ID連携が設定済のチームと全く同じ項目で構成されているチームに、同じIDへの連携を設定できます。
          </Typography>
          <Typography variant="caption" color="text.secondary">
            対象課: {divisionLabel || `department_id=${division}`}
          </Typography>

          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5, minHeight: 280 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                連携済みチーム（選択）
              </Typography>
              {loadingSourceTeams ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">読込中...</Typography>
                </Box>
              ) : linkedSourceTeams.length === 0 ? (
                <Alert severity="info">この課にEnlight ID連携済みチームがありません。</Alert>
              ) : (
                <RadioGroup
                  value={selectedSourceTeamId}
                  onChange={(e) => {
                    if (!canEdit) return;
                    setSelectedSourceTeamId(String(e.target.value ?? ''));
                    setSelectedTargetTeamIds({});
                  }}
                >
                  {linkedSourceTeams.map((row) => (
                    <FormControlLabel
                      key={row.teamId}
                      value={String(row.teamId)}
                      control={<Radio size="small" disabled={!canEdit} />}
                      label={
                        <Box>
                          <Typography variant="body2">{row.teamName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.enlightTeam}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, minHeight: 280 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                項目が一致するチーム（複数選択）
              </Typography>
              {loadingAttributes ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">読込中...</Typography>
                </Box>
              ) : !selectedSourceTeamId ? (
                <Alert severity="info">左側からチームを選択してください。</Alert>
              ) : candidateTargetTeams.length === 0 ? (
                <Alert severity="info">一致するチームはありません。</Alert>
              ) : (
                <Stack spacing={0.5}>
                  {candidateTargetTeams.map((row) => {
                    const summary = teamDocsetSummaryByTeamId[row.teamId];
                    const isAlreadyLinked = Boolean(summary?.hasLinkedEnlightTeam);
                    const linkedEnlightTeam = String(summary?.linkedEnlightTeam ?? '').trim();
                    return (
                      <FormControlLabel
                        key={row.teamId}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(selectedTargetTeamIds[row.teamId])}
                            disabled={!canEdit || isAlreadyLinked}
                            onChange={handleCheckTarget(row.teamId)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{row.teamName}</Typography>
                            {isAlreadyLinked ? (
                              <Typography variant="caption" color="text.secondary">
                                {(linkedEnlightTeam ? `${linkedEnlightTeam}と連携済み` : '連携済み') + 'のため選択不可'}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Box>
          {copyResultMessage ? (
            <Alert severity={copyResultMessage.includes('失敗') ? 'error' : 'success'}>
              {copyResultMessage}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleExecuteCopy}
          variant="contained"
          disabled={!canEdit || submittingCopy || !selectedSourceTeamId || selectedTargetTeamIdList.length === 0}
        >
          {submittingCopy ? '実行中...' : 'ID連携を行う'}
        </Button>
        <Button onClick={onClose} variant="outlined" color="secondary">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnlightIDConnectionCopyDialog;
